import React, { Component } from "react";
import { getUserId, getAccessToken, logRefreshToken } from "../../token/UserToken";
import { useAuth } from "../../context/AuthContext";

interface VanillaWrapperProps {
    token: string
}

interface VanillaWrapperState {
    showWebhookTest: boolean;
    currentToken: string;
    isInitialized: boolean;
}

// Hook personalizado para usar dentro do componente de classe
const useAuthWrapper = () => {
    const { ensureValidToken } = useAuth();
    return { ensureValidToken };
};

// Componente funcional que encapsula o hook
const VanillaWrapperWithAuth: React.FC<VanillaWrapperProps> = (props) => {
    const { ensureValidToken } = useAuthWrapper();
    
    return <VanillaWrapperClass {...props} ensureValidToken={ensureValidToken} />;
};

interface VanillaWrapperClassProps extends VanillaWrapperProps {
    ensureValidToken: () => Promise<string | null>;
}

class VanillaWrapperClass extends Component<VanillaWrapperClassProps, VanillaWrapperState> {
    public shadowHost: any = null;
    private initializationPromise: Promise<void> | null = null;
    
    constructor(props: VanillaWrapperClassProps) {
        super(props);
        this.shadowHost = React.createRef();
        this.state = {
            showWebhookTest: false,
            currentToken: props.token,
            isInitialized: false
        };
        
        logRefreshToken('INFO', 'VanillaWrapper inicializado');
    }

    componentDidMount(): void {
        const CLASSE = this;
        logRefreshToken('INFO', 'VanillaWrapper componentDidMount iniciado');
        
        const shadowRoot = CLASSE.shadowHost.current.attachShadow({ mode: "open" });
        shadowRoot.innerHTML = `
            <link href="https://cdnjs.cloudflare.com/ajax/libs/devextreme/22.2.3/css/dx.common.css" rel="stylesheet" /> 
            <link href="https://cdnjs.cloudflare.com/ajax/libs/devextreme/22.2.3/css/dx.light.compact.css" rel="stylesheet" /> 
            <link href="./epedidos-web/epedidos.89f74bd29e43f7cf8a1c.css" rel="stylesheet" /> 
            <div id="wrapper-epedidos"> 
            </div>
            <!-- 
            <div id="webhook-test-toggle" style="
                position: fixed;
                top: 20px;
                right: 20px;
                z-index: 10000;
                background: #007bff;
                color: white;
                padding: 10px 15px;
                border-radius: 25px;
                cursor: pointer;
                box-shadow: 0 4px 15px rgba(0,123,255,0.3);
                font-family: Arial, sans-serif;
                font-size: 14px;
                font-weight: 600;
                transition: all 0.3s ease;
            ">
                🔗 Teste Webhook
            </div>
            -->
        `;

        // Comentado: Adicionar evento de clique para o botão de teste
        /*
        const toggleButton = shadowRoot.querySelector('#webhook-test-toggle');
        toggleButton?.addEventListener('click', () => {
            logRefreshToken('INFO', 'Botão de teste webhook clicado - redirecionando');
            window.open('/webhook-test', '_blank');
        });
        */

        setTimeout(() => {
            let element = shadowRoot.querySelector("#wrapper-epedidos");
            if (element) {
                (element as HTMLElement).style.display = "flex";
                (element as HTMLElement).style.flexDirection = "column";
                (element as HTMLElement).style.flexGrow = "1";

                // Garantir que temos um token válido antes de inicializar
                this.initializeWithValidToken(element);
            }
        }, 1000);
    }

    // Método para garantir token válido antes de inicializar
    private async initializeWithValidToken(element: Element) {
        // Evitar múltiplas inicializações simultâneas
        if (this.state.isInitialized || this.initializationPromise) {
            logRefreshToken('INFO', 'Inicialização já em andamento ou concluída');
            return this.initializationPromise;
        }

        this.initializationPromise = this.performInitialization(element);
        return this.initializationPromise;
    }

    private async performInitialization(element: Element) {
        try {
            // Garantir que temos um token válido
            const validToken = await this.props.ensureValidToken();
            
            if (!validToken) {
                logRefreshToken('ERROR', 'Não foi possível obter um token válido');
                console.error('Não foi possível obter um token válido');
                return;
            }

            logRefreshToken('INFO', 'Inicializando aplicação com token válido');
            console.log('Inicializando aplicação com token válido');
            
            // Atualizar estado com token atual e marcar como inicializado
            this.setState({ 
                currentToken: validToken,
                isInitialized: true
            });
            
            // Inicializar a aplicação com o token válido
            const fnInicializa: any = (window as any)["inicializar"];
            if (fnInicializa) {
                fnInicializa({
                    "appToken": validToken,
                    "appLogotipo": "https://bibliotecas.cosmospro.com.br/cosmos/rede-nossa-farma/mmc/img/logo-rede-nossa-farma-64.png",
                    "companyUid": "BZP",
                    "userId": getUserId(),
                    "companyId": 1,
                    "companyLogo": "B46B44207EA268EDC270134B058CD3B6.png",
                    "isOfficial": true,
                    "appPortalUrl": "https://portal.cosmospro.com.br:9191"
                });
            }
            
            const fnRender: any = (window as any)["renderApp"];
            if (fnRender) {
                setTimeout(() => { fnRender(element); }, 2000);
            }
            
            logRefreshToken('INFO', 'Aplicação inicializada com sucesso');
            
        } catch (error) {
            logRefreshToken('ERROR', 'Erro ao inicializar com token válido');
            console.error('Erro ao inicializar com token válido:', error);
            this.setState({ isInitialized: false });
            this.initializationPromise = null;
        }
    }

    toggleWebhookTest = () => {
        logRefreshToken('INFO', `Alternando exibição do teste webhook: ${!this.state.showWebhookTest}`);
        this.setState(prevState => ({
            showWebhookTest: !prevState.showWebhookTest
        }));
    }

    componentWillUnmount(): void { 
        logRefreshToken('INFO', 'VanillaWrapper será desmontado');
        console.log("componentWillUnmount");
        
        // Limpar Promise de inicialização
        this.initializationPromise = null;
    }

    // Evitar re-renderizações desnecessárias
    shouldComponentUpdate(nextProps: VanillaWrapperClassProps, nextState: VanillaWrapperState): boolean {
        // Só atualizar se realmente houver mudanças significativas
        return (
            nextProps.token !== this.props.token ||
            nextState.showWebhookTest !== this.state.showWebhookTest ||
            nextState.isInitialized !== this.state.isInitialized
        );
    } 

    render() { 
        return <div ref={this.shadowHost} className="shadowHost"></div>; 
    }
} 

export default VanillaWrapperWithAuth;
